import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, CreditCard, Receipt, ExternalLink, Globe, Loader2, AlertCircle, Trash2, Briefcase, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Helmet } from '@/components/Helmet';
import { LogoShield } from '@/components/LogoShield';
import { getDomainLabel, getDomainPrice } from '@/components/portal/pricing';
import { CANCELLATION_FEES, type TierType } from '@/components/portal/TierSelection';
import { StatusBadge } from '@/components/portal/StatusBadge';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { useCurrency } from '@/hooks/use-currency';
import { openExternalUrl } from '@/lib/navigation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PortfolioPricingStatusCard } from '@/components/portal/PortfolioPricingStatusCard';
import { PortfolioPricingApplicationForm } from '@/components/portal/PortfolioPricingApplicationForm';
import { usePortfolioPricingApplication } from '@/hooks/use-portfolio-pricing-application';
import { useReviewedPricingSchedule } from '@/hooks/use-reviewed-pricing-schedule';
import {
  DEFAULT_PORTFOLIO_PRICING_FORM,
  normalizePortfolioPricingApplication,
  type PortfolioPricingApplicationInput,
  type PortfolioPricingApplicationRow,
  type PortfolioPricingUiStatus,
  validatePortfolioPricingApplication,
} from '@/lib/portfolioPricing';
import {
  buildPortfolioPricingAnalyticsProps,
  capturePortfolioPricingEvent,
} from '@/lib/portfolioPricingAnalytics';

interface Invoice {
  id: string;
  number: string;
  date: number;
  amount: number;
  amountPaid?: number;
  amountOwing?: number;
  currency: string;
  status: string;
  description: string;
  hostedUrl: string;
  pdfUrl: string;
}

const applicationToFormInput = (
  application: PortfolioPricingApplicationRow | null | undefined,
  companyName: string | null | undefined,
): PortfolioPricingApplicationInput => {
  if (!application) {
    return {
      ...DEFAULT_PORTFOLIO_PRICING_FORM,
      businessName: companyName || '',
    };
  }

  return {
    businessName: application.business_name || companyName || '',
    companyUrl: application.company_url || '',
    roleAccountType: application.role_account_type || '',
    approximatePortfolioSize: application.approximate_portfolio_size || '',
    currentSiteCount: application.current_site_count?.toString() || '',
    expectedSiteCount: application.expected_site_count?.toString() || '',
    siteOwnershipType:
      application.site_ownership_type === 'own' ||
      application.site_ownership_type === 'client' ||
      application.site_ownership_type === 'mixed'
        ? application.site_ownership_type
        : 'mixed',
    useCaseSummary: application.use_case_summary || '',
    primaryMarket: application.primary_market || '',
    pricingNeed:
      application.pricing_need === 'internal' ||
      application.pricing_need === 'agency_client' ||
      application.pricing_need === 'reseller_partner'
        ? application.pricing_need
        : 'agency_client',
    expectedOnboardingTimeframe: application.expected_onboarding_timeframe || '',
  };
};

export default function Billing() {
  const { user, loading } = useAuth();
  const { isImpersonating, activeCustomerId, activeCustomerEmail, activeCustomerName, clearImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const { checking } = useRequireMfa();
  const queryClient = useQueryClient();
  const [billingLoading, setBillingLoading] = useState(false);
  const [resumingDomainId, setResumingDomainId] = useState<string | null>(null);
  const [deletingDomainId, setDeletingDomainId] = useState<string | null>(null);
  const [confirmDomainInput, setConfirmDomainInput] = useState('');
  const [confirmCancelFees, setConfirmCancelFees] = useState<Record<string, boolean>>({});
  const [impersonationForceRemove, setImpersonationForceRemove] = useState<Record<string, boolean>>({});
  const [impersonationForceReason, setImpersonationForceReason] = useState<Record<string, string>>({});
  const [showPortfolioPricingForm, setShowPortfolioPricingForm] = useState(false);
  const [showApplicationDetails, setShowApplicationDetails] = useState(false);
  const [submittingPortfolioPricing, setSubmittingPortfolioPricing] = useState(false);
  const [portfolioPricingForm, setPortfolioPricingForm] = useState<PortfolioPricingApplicationInput>(DEFAULT_PORTFOLIO_PRICING_FORM);
  const { formatCurrency } = useCurrency();
  const isImpersonationView = Boolean(isImpersonating && activeCustomerId);
  const subjectUserId = isImpersonationView ? activeCustomerId : user?.id;
  const isPaymentNeeded = (status: string) =>
    status === 'payment_needed' || status === 'payment_incomplete' || status === 'pending_payment';
  const getTierType = (domain: { stripe_price_id?: string | null; stripePriceId?: string | null; promo_price?: number | null; promoPrice?: number | null; tier?: string | null }): TierType => {
    if (domain.tier === 'basic' || domain.tier === 'essential' || domain.tier === 'premium') {
      return domain.tier;
    }
    const label = getDomainLabel(domain).toLowerCase();
    if (label.includes('basic')) return 'basic';
    if (label.includes('essential')) return 'essential';
    return 'premium';
  };

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });
  const showAdminAggregates = isAdmin === true && !isImpersonationView;

  // Admin accounts summary
  const { data: adminAccounts } = useQuery({
    queryKey: ['admin-billing-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-list-accounts');
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data?.accounts || [];
    },
    enabled: !!user && showAdminAggregates,
  });

  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains', subjectUserId, isImpersonationView ? 'impersonation' : 'self'],
    queryFn: async () => {
      if (!subjectUserId) return [];
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('user_id', subjectUserId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!subjectUserId,
  });

  const { data: profile } = useQuery({
    queryKey: ['profile-payment-bypass', subjectUserId],
    queryFn: async () => {
      if (!subjectUserId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('payment_lock_bypass, account_kind, company_name, full_name')
        .eq('user_id', subjectUserId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!subjectUserId,
  });

  const paymentBypassUser = Boolean(profile?.payment_lock_bypass);
  const accountKind = profile?.account_kind === 'dev' ? 'dev' : 'standard';

  const {
    data: portfolioPricingApplication,
    isLoading: portfolioPricingApplicationLoading,
  } = usePortfolioPricingApplication(subjectUserId, Boolean(subjectUserId) && !showAdminAggregates);
  const { data: latestReviewedPricingSchedule } = useReviewedPricingSchedule(
    subjectUserId,
    Boolean(subjectUserId) && !showAdminAggregates,
  );

  const portfolioPricingUiStatus: PortfolioPricingUiStatus = portfolioPricingApplication?.status
    ? (portfolioPricingApplication.status as PortfolioPricingUiStatus)
    : accountKind === 'dev'
      ? 'approved'
      : 'none';

  const canSubmitPortfolioPricingApplication =
    !isImpersonationView &&
    !showAdminAggregates &&
    (portfolioPricingUiStatus === 'none' || portfolioPricingUiStatus === 'declined');

  useEffect(() => {
    if (showAdminAggregates || !subjectUserId) return;
    capturePortfolioPricingEvent('portfolio_pricing_section_viewed', {
      page: 'portal_billing',
      status: portfolioPricingUiStatus,
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: portfolioPricingApplication?.submission_source,
        status: portfolioPricingUiStatus,
        accountKind,
        campaignSource: portfolioPricingApplication?.signup_source,
        approximatePortfolioSize: portfolioPricingApplication?.approximate_portfolio_size,
        currentSiteCount: portfolioPricingApplication?.current_site_count,
        expectedSiteCount: portfolioPricingApplication?.expected_site_count,
      }),
    });
  }, [accountKind, portfolioPricingApplication, portfolioPricingUiStatus, showAdminAggregates, subjectUserId]);

  useEffect(() => {
    if (showAdminAggregates || !subjectUserId || portfolioPricingUiStatus === 'none') return;
    capturePortfolioPricingEvent('portfolio_pricing_status_viewed', {
      page: 'portal_billing',
      status: portfolioPricingUiStatus,
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: portfolioPricingApplication?.submission_source,
        status: portfolioPricingUiStatus,
        accountKind,
        campaignSource: portfolioPricingApplication?.signup_source,
        approximatePortfolioSize: portfolioPricingApplication?.approximate_portfolio_size,
        currentSiteCount: portfolioPricingApplication?.current_site_count,
        expectedSiteCount: portfolioPricingApplication?.expected_site_count,
      }),
    });
  }, [accountKind, portfolioPricingApplication, portfolioPricingUiStatus, showAdminAggregates, subjectUserId]);

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', subjectUserId, isImpersonationView ? 'impersonation' : 'self'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      
      const { data, error } = await supabase.functions.invoke('list-invoices', {
        body: isImpersonationView && activeCustomerId ? { targetUserId: activeCustomerId } : {},
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      return data?.invoices as Invoice[] || [];
    },
    enabled: !!subjectUserId && !showAdminAggregates,
  });

  const handleSyncSubscription = async () => {
    try {
      await supabase.functions.invoke('sync-subscription-quantity', {
        body: isImpersonationView && activeCustomerId ? { targetUserId: activeCustomerId } : {},
      });
      queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
      queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
      toast.success('Billing refreshed from Stripe.');
    } catch (err: any) {
      console.error('Subscription sync failed:', err);
      toast.error('Could not sync subscription quantity.');
    }
  };

  // NOTE: subscription quantity is now synced by backend flows (add/delete domain). If needed, a manual sync button can call
  // sync-subscription-quantity from a trusted environment to avoid browser CORS issues.

  const handleManageBilling = async () => {
    if (showAdminAggregates) {
      openExternalUrl('https://dashboard.stripe.com/');
      return;
    }
    setBillingLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in again to access billing.');
        navigate('/portal/auth');
        return;
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: isImpersonationView && activeCustomerId ? { targetUserId: activeCustomerId } : {},
      });
      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      if (data?.url) {
        openExternalUrl(data.url);
        if (data.type === 'setup') {
          toast.info('Please add a payment method to continue.');
        }
      }
    } catch (err: any) {
      console.error('Billing portal error:', err);
      toast.error('Unable to open billing portal. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  const updatePortfolioPricingForm = <K extends keyof PortfolioPricingApplicationInput>(
    key: K,
    value: PortfolioPricingApplicationInput[K],
  ) => {
    setPortfolioPricingForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const openPortfolioPricingForm = () => {
    const seed =
      portfolioPricingApplication?.status === 'declined'
        ? applicationToFormInput(portfolioPricingApplication, profile?.company_name)
        : applicationToFormInput(null, profile?.company_name);
    setPortfolioPricingForm(seed);
    setShowPortfolioPricingForm(true);
    capturePortfolioPricingEvent('portfolio_pricing_apply_clicked', {
      page: 'portal_billing',
      status: portfolioPricingUiStatus,
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: 'billing',
        status: portfolioPricingUiStatus,
        accountKind,
        campaignSource: portfolioPricingApplication?.signup_source,
      }),
    });
    capturePortfolioPricingEvent('portfolio_pricing_application_started', {
      page: 'portal_billing',
      mode: portfolioPricingApplication?.status === 'declined' ? 'reapply' : 'apply',
      ...buildPortfolioPricingAnalyticsProps({
        submissionSource: 'billing',
        status: portfolioPricingUiStatus,
        accountKind,
        campaignSource: portfolioPricingApplication?.signup_source,
      }),
    });
  };

  const handleSubmitPortfolioPricingApplication = async () => {
    try {
      validatePortfolioPricingApplication(portfolioPricingForm);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Please check the application details.');
      return;
    }

    setSubmittingPortfolioPricing(true);
    try {
      const normalizedForm = normalizePortfolioPricingApplication(portfolioPricingForm);
      const { data, error } = await supabase.functions.invoke('submit-portfolio-pricing-application', {
        body: normalizedForm,
      });
      if (error) throw error;
      if (data?.error) {
        throw new Error(data.error);
      }

      await queryClient.invalidateQueries({ queryKey: ['portfolio-pricing-application', subjectUserId] });
      await queryClient.invalidateQueries({ queryKey: ['admin-billing-accounts'] });
      await queryClient.invalidateQueries({ queryKey: ['profile-payment-bypass', subjectUserId] });
      await queryClient.invalidateQueries({ queryKey: ['profile', subjectUserId] });
      await queryClient.invalidateQueries({ queryKey: ['notifications', subjectUserId] });
      setShowPortfolioPricingForm(false);
      setShowApplicationDetails(false);

      const isReapply = portfolioPricingApplication?.status === 'declined';
      capturePortfolioPricingEvent(
        isReapply
          ? 'portfolio_pricing_application_reapplied'
          : 'portfolio_pricing_application_submitted',
        {
          page: 'portal_billing',
          mode: isReapply ? 'reapply' : 'apply',
          ...buildPortfolioPricingAnalyticsProps({
            submissionSource: 'billing',
            status: 'pending_review',
            accountKind,
            campaignSource: portfolioPricingApplication?.signup_source,
            approximatePortfolioSize: normalizedForm.approximatePortfolioSize,
            currentSiteCount: normalizedForm.currentSiteCount,
            expectedSiteCount: normalizedForm.expectedSiteCount,
          }),
        },
      );

      toast.success(
        isReapply
          ? 'Updated portfolio pricing application submitted for review.'
          : 'Portfolio pricing application submitted for review.',
      );
    } catch (error: any) {
      console.error('Portfolio pricing application submission failed:', error);
      toast.error(error?.message || 'Unable to submit your portfolio pricing application.');
    } finally {
      setSubmittingPortfolioPricing(false);
    }
  };

  const formatInvoiceCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const handleResumePayment = async (domainId: string) => {
    setResumingDomainId(domainId);
    try {
      if (isImpersonationView) {
        if (!activeCustomerId) {
          throw new Error('No active customer selected for impersonation.');
        }
        const { data: impersonationContext, error: contextError } = await supabase.functions.invoke('admin-impersonation-context', {
          body: { customerId: activeCustomerId },
        });
        if (contextError || impersonationContext?.error) {
          throw contextError || new Error(impersonationContext?.error || 'Unable to load customer impersonation context.');
        }
        const requestId = impersonationContext?.domains?.find((domain: any) => domain?.id === domainId)?.pending_purchase_request?.id;
        if (!requestId) {
          throw new Error('No pending purchase request was found for this domain.');
        }
        const { data, error } = await supabase.functions.invoke('admin-resend-domain-purchase-request', {
          body: { requestId },
        });
        if (error || data?.error) {
          throw error || new Error(data?.error || 'Unable to resend customer payment request.');
        }
        toast.success('Customer payment email resent.');
        queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
        queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
        queryClient.invalidateQueries({ queryKey: ['admin-impersonation-context', activeCustomerId] });
        return;
      }

      const { data, error } = await supabase.functions.invoke('resume-checkout', {
        body: { domainId },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.assign(data.url);
        toast.success('Redirecting to checkout to complete payment.');
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err: any) {
      console.error('Resume checkout failed:', err);
      toast.error(err?.message || (isImpersonationView ? 'Unable to resend customer payment email.' : 'Unable to resume payment. Please try again.'));
    } finally {
      setResumingDomainId(null);
    }
  };

  const handleDeleteDomain = async (domain: any) => {
    if (confirmDomainInput.trim().toLowerCase() !== domain.domain_name.trim().toLowerCase()) {
      toast.error('Please type the domain name exactly to confirm removal.');
      return;
    }

    if (isImpersonationView) {
      const forceRemove = Boolean(impersonationForceRemove[domain.id]);
      const forceReason = (impersonationForceReason[domain.id] || '').trim();
      if (forceRemove && !forceReason) {
        toast.error('Provide a reason before force removing this domain.');
        return;
      }

      setDeletingDomainId(domain.id);
      try {
        const { data, error } = await supabase.functions.invoke('admin-domain-removal-request', {
          body: {
            action: forceRemove ? 'force-remove' : 'request',
            domainId: domain.id,
            reason: forceRemove ? forceReason : undefined,
          },
        });
        if (error || data?.error) {
          throw error || new Error(data?.error || 'Unable to process domain removal.');
        }
        if (forceRemove) {
          toast.success('Domain force removed. Customer and support were notified.');
        } else {
          toast.success('Confirmation email sent to the customer. No cancellation fee was charged.');
        }
        queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
        queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
      } catch (err: any) {
        console.error('Impersonation delete domain error:', err);
        toast.error(err?.message || 'Failed to process domain removal');
      } finally {
        setDeletingDomainId(null);
        setConfirmDomainInput('');
        setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: false }));
        setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
      }
      return;
    }

    setDeletingDomainId(domain.id);
    try {
      let chargeResult: { charged?: boolean; reason?: string; error?: string } | null = null;
      if (!isPaymentNeeded(domain.status)) {
        const { data, error: chargeError } = await supabase.functions.invoke('charge-cancellation-fee', {
          body: { domainName: domain.domain_name, subscriptionId: domain.stripe_subscription_id },
        });
        chargeResult = data || null;

        if (chargeError) {
          console.error('Cancellation fee charge error:', chargeError);
          toast.error('Failed to process cancellation fee. Please try again.');
          setDeletingDomainId(null);
          return;
        }

        if (chargeResult?.error) {
          console.error('Cancellation fee error:', chargeResult.error);
          toast.error(`Failed to process cancellation fee: ${chargeResult.error}`);
          setDeletingDomainId(null);
          return;
        }

        if (!chargeResult?.charged) {
          if (chargeResult?.reason === 'free_trial') {
            toast.info('This domain is still in the 7-day free trial. No cancellation fee will be charged.');
          } else if (chargeResult?.reason === 'no_fee') {
            toast.info('This reviewed pricing domain does not have a cancellation fee.');
          } else {
            toast.error('Unable to charge cancellation fee. Please update your payment method.');
            setDeletingDomainId(null);
            return;
          }
        }
      }

      const { error } = await supabase
        .from('domains')
        .update({
          status: 'cancelled',
          deleted_at: new Date().toISOString(),
        })
        .eq('id', domain.id);

      if (error) throw error;

      await supabase.functions.invoke('notify-domain-change', {
        body: {
          action: 'deleted',
          domains: [{
            domainName: domain.domain_name,
            wpAdminUrl: domain.wp_admin_url,
            status: domain.status,
            createdAt: domain.created_at,
          }],
          userEmail: user!.email,
          changes: isPaymentNeeded(domain.status)
            ? `Domain "${domain.domain_name}" was deleted by the customer while payment was incomplete. No cancellation fee was charged.`
            : chargeResult?.reason === 'free_trial'
              ? `Domain "${domain.domain_name}" was deleted by the customer during the 7-day free trial. No cancellation fee was charged.`
              : chargeResult?.reason === 'no_fee'
                ? `Domain "${domain.domain_name}" was deleted under reviewed pricing terms. No cancellation fee was charged.`
                : `Domain "${domain.domain_name}" was deleted by the customer via billing. Cancellation fee was charged.`,
        },
      });

      queryClient.invalidateQueries({ queryKey: ['domains', subjectUserId] });
      queryClient.invalidateQueries({ queryKey: ['invoices', subjectUserId] });
      toast.success(
        isPaymentNeeded(domain.status)
          ? 'Domain deleted. No cancellation fee due to incomplete payment.'
          : chargeResult?.reason === 'free_trial'
            ? 'Domain deleted during the 7-day free trial. No cancellation fee was charged.'
            : chargeResult?.reason === 'no_fee'
              ? 'Domain deleted. No cancellation fee applies under the reviewed pricing terms.'
              : 'Domain deleted. Cancellation fee charged.'
      );
    } catch (err: any) {
      console.error('Delete domain error:', err);
      toast.error('Failed to delete domain');
    } finally {
      setDeletingDomainId(null);
      setConfirmDomainInput('');
    }
  };

  // Derived values (keep before conditional returns to preserve hook order)
  const totalDomains = domains?.length || 0;
  const tierBreakdown = (domains || []).reduce<Record<string, { count: number; price: number; label: string }>>((acc, domain) => {
    const price = getDomainPrice(domain);
    const label = getDomainLabel(domain);
    const key = `${label}-${price}`;
    if (!acc[key]) acc[key] = { count: 0, price, label };
    acc[key].count += 1;
    return acc;
  }, {});

  const monthlyTotal = (domains || []).reduce((sum, domain) => {
    return sum + getDomainPrice(domain);
  }, 0);

  const adminSubscriptions = useMemo(() => {
    if (!isAdmin || !adminAccounts) return [];
    return adminAccounts.map((acct: any) => {
      const byTier = new Map<string, { label: string; count: number; price: number }>();
      (acct.domains || []).forEach((d: any) => {
        const price = getDomainPrice(d);
        const label = getDomainLabel(d);
        const key = `${label}-${price}`;
        const existing = byTier.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          byTier.set(key, { label, count: 1, price });
        }
      });
      return {
        email: acct.email || 'Unknown email',
        fullName: acct.fullName || null,
        breakdown: Array.from(byTier.values()),
      };
    });
  }, [isAdmin, adminAccounts]);

  const filteredInvoices = (invoicesData || []).filter(
    (invoice) => invoice.status !== 'draft' && invoice.status !== 'void'
  );
  const impersonationHomePath = isImpersonationView && activeCustomerId
    ? `/portal/admin/impersonate/${activeCustomerId}`
    : '/portal';

  if (checking || loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Billing & Subscriptions | ShadowScan Portal</title>
          <meta
            name="description"
            content="Review ShadowScan billing details, invoices, and subscription quantities for your protected WordPress domains, and update payment methods securely."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Billing & Subscriptions | ShadowScan Portal</title>
        <meta
          name="description"
          content="View your ShadowScan subscription totals, invoice history, and payment settings for all protected domains, and refresh billing directly from the portal."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(impersonationHomePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {isImpersonationView && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                clearImpersonation();
                navigate('/portal/admin');
              }}
            >
              Exit customer view
            </Button>
          )}
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Billing & Invoices</span>
              {isImpersonationView && (
                <span className="text-xs text-[hsl(var(--ss-warning))]">
                  Customer view: {activeCustomerName || activeCustomerEmail || activeCustomerId}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        {/* Billing Summary */}
        <Card className="cyber-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              {showAdminAggregates ? 'Billing Summary (All Users)' : 'Billing Summary'}
            </CardTitle>
            <CardDescription>
              {showAdminAggregates
                ? 'Aggregated totals across all customers.'
                : isImpersonationView
                  ? 'Customer billing view while impersonating. Actions are logged as admin-initiated.'
                : 'Overview of your current subscription and billing. Billed on the 1st of each month.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total Domains</span>
                  <span className="font-mono font-bold">
                    {showAdminAggregates
                      ? (adminAccounts || []).reduce(
                          (sum: number, acct: any) => sum + (acct.domains?.length || 0),
                          0
                        )
                      : totalDomains}
                  </span>
                </div>
                <div className="space-y-2">
                  <span className="text-muted-foreground block">Plan Breakdown</span>
                  {showAdminAggregates ? (
                    <span className="text-sm text-muted-foreground">All customer plans</span>
                  ) : Object.entries(tierBreakdown).length === 0 ? (
                    <span className="text-sm text-muted-foreground">No active domains</span>
                  ) : (
                    Object.entries(tierBreakdown).map(([key, info]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="font-medium">{info.label}</span>
                        <span className="font-mono">
                          {info.count} x {formatCurrency(info.price)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-border">
                  <span className="font-medium">Monthly Total</span>
                  <span className="text-xl font-mono font-bold text-primary">
                    {formatCurrency(
                      showAdminAggregates
                        ? (adminAccounts || []).reduce((sum: number, acct: any) => {
                            return sum + (acct.domains || []).reduce((dSum: number, d: any) => dSum + getDomainPrice(d), 0);
                          }, 0)
                        : monthlyTotal,
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Estimated · Billed in AUD on the 1st</p>
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-sm text-muted-foreground mb-4">
                  {showAdminAggregates
                    ? 'Open the Stripe admin dashboard to manage customer billing, payment methods, and invoices.'
                    : isImpersonationView
                      ? 'Open this customer billing portal while impersonating. Access is audited.'
                    : 'Manage your payment method, view invoices, and update billing details through the Stripe customer portal.'}
                </p>
                <div className="flex gap-2 flex-col sm:flex-row">
                  <Button onClick={handleManageBilling} className="w-full" disabled={billingLoading}>
                    {billingLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    {billingLoading
                      ? 'Loading...'
                      : showAdminAggregates
                      ? 'Open Stripe Dashboard'
                      : isImpersonationView
                        ? 'Open Customer Billing Portal'
                        : 'Manage Payment Method'}
                    {!billingLoading && <ExternalLink className="h-4 w-4 ml-2" />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {!showAdminAggregates && (
          <Card className="cyber-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                Portfolio pricing
              </CardTitle>
              <CardDescription>
                Review your current pricing path, apply for reviewed portfolio pricing, and
                keep the standard self-serve path available while review is pending.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {portfolioPricingApplicationLoading ? (
                <div className="text-sm text-muted-foreground">Loading pricing path…</div>
              ) : portfolioPricingApplication ? (
                <>
                  <PortfolioPricingStatusCard
                    application={portfolioPricingApplication}
                    activateDomainHref="/portal/add-domain"
                    reviewedPricingSchedule={latestReviewedPricingSchedule}
                  />

                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowApplicationDetails((current) => !current)}
                    >
                      {showApplicationDetails ? 'Hide application details' : 'View application details'}
                    </Button>
                    {portfolioPricingApplication.status === 'declined' && !isImpersonationView ? (
                      <Button onClick={openPortfolioPricingForm}>
                        Apply again with updated details
                      </Button>
                    ) : null}
                  </div>

                  {showApplicationDetails ? (
                    <Card className="border-border/60 bg-muted/20">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Application details</CardTitle>
                        <CardDescription>
                          Review the details currently attached to your latest portfolio pricing application.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="grid gap-4 text-sm md:grid-cols-2">
                        <div>
                          <div className="text-xs text-muted-foreground">Business</div>
                          <div>{portfolioPricingApplication.business_name}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Website</div>
                          <div>{portfolioPricingApplication.company_url || 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Role</div>
                          <div>{portfolioPricingApplication.role_account_type}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Portfolio size</div>
                          <div>{portfolioPricingApplication.approximate_portfolio_size || 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Current managed sites</div>
                          <div>{portfolioPricingApplication.current_site_count ?? 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Expected ShadowScan sites</div>
                          <div>{portfolioPricingApplication.expected_site_count}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Site ownership</div>
                          <div>{portfolioPricingApplication.site_ownership_type}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Pricing need</div>
                          <div>{portfolioPricingApplication.pricing_need || 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Primary market</div>
                          <div>{portfolioPricingApplication.primary_market || 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Expected onboarding timeframe</div>
                          <div>{portfolioPricingApplication.expected_onboarding_timeframe || 'Not provided'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Submitted via</div>
                          <div>{portfolioPricingApplication.submission_source}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Submitted</div>
                          <div>{new Date(portfolioPricingApplication.submitted_at).toLocaleDateString()}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-xs text-muted-foreground">Use case summary</div>
                          <div className="whitespace-pre-wrap">{portfolioPricingApplication.use_case_summary}</div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                </>
              ) : accountKind === 'dev' ? (
                <Card className="border-border/70 bg-card/80">
                  <CardHeader>
                    <CardTitle className="text-lg">Reviewed pricing path active</CardTitle>
                    <CardDescription>
                      Your account is already on a reviewed pricing path. Contact support if your
                      account structure changes materially.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="outline" size="sm">
                      <a href="mailto:support@shadowscan.com.au?subject=ShadowScan%20portfolio%20pricing">
                        <Mail className="h-4 w-4" />
                        Contact support
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-border/70 bg-card/80">
                  <CardHeader>
                    <CardTitle className="text-lg">Standard self-serve path</CardTitle>
                    <CardDescription>
                      If you manage multiple client or portfolio sites, you can apply for reviewed
                      portfolio pricing. Admin review is still required before any private pricing
                      path is enabled.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-lg border border-border/60 bg-background/60 p-4 text-sm leading-relaxed text-muted-foreground">
                      Account creation and standard billing stay available while portfolio pricing is
                      reviewed. Billing begins when domains are activated unless ShadowScan approves
                      a different pricing path.
                    </div>
                    {isImpersonationView ? (
                      <div className="text-sm text-muted-foreground">
                        Portfolio pricing applications must be submitted from the customer account.
                      </div>
                    ) : (
                      <Button onClick={openPortfolioPricingForm}>
                        Apply for portfolio pricing
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {showPortfolioPricingForm && canSubmitPortfolioPricingApplication ? (
                <Card className="border-border/70 bg-muted/20">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {portfolioPricingApplication?.status === 'declined'
                        ? 'Update your portfolio pricing application'
                        : 'Apply for portfolio pricing'}
                    </CardTitle>
                    <CardDescription>
                      Reuse the same account details ShadowScan reviews at signup. Approval is handled
                      by admins and private pricing is never assigned automatically.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <PortfolioPricingApplicationForm
                      value={portfolioPricingForm}
                      onChange={updatePortfolioPricingForm}
                      disabled={submittingPortfolioPricing}
                    />
                    <div className="rounded-lg border border-border/60 bg-background/60 p-4 text-sm leading-relaxed text-muted-foreground">
                      Your application will be submitted for admin review. While review is pending,
                      the standard self-serve path stays available.
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        onClick={() => void handleSubmitPortfolioPricingApplication()}
                        disabled={submittingPortfolioPricing}
                      >
                        {submittingPortfolioPricing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Submitting…
                          </>
                        ) : portfolioPricingApplication?.status === 'declined' ? (
                          'Submit updated application'
                        ) : (
                          'Submit application'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowPortfolioPricingForm(false)}
                        disabled={submittingPortfolioPricing}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Invoices Section */}
        {!showAdminAggregates && (
          <Card className="cyber-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                Invoice History
              </CardTitle>
              <CardDescription>
                Your recent invoices and payment history.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invoicesLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                  Loading invoices...
                </div>
              ) : filteredInvoices.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between px-4 text-xs uppercase tracking-wide text-muted-foreground">
                    <div className="flex items-center gap-6">
                      <span className="min-w-[100px]">Date</span>
                      <span className="min-w-[80px]">Amount Paid</span>
                      <span className="min-w-[100px]">Amount Owing</span>
                      <span>Status</span>
                      <span className="hidden md:inline">Description</span>
                    </div>
                    <span className="mr-2">Action</span>
                  </div>
                  {filteredInvoices.map((invoice) => (
                    <div 
                      key={invoice.id} 
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                    <div className="flex items-center gap-6">
                      <div className="text-sm text-muted-foreground min-w-[100px]">
                        {format(new Date(invoice.date * 1000), 'd MMM yyyy')}
                      </div>
                      <div className="font-mono font-medium min-w-[80px]">
                        {formatInvoiceCurrency(invoice.amountPaid ?? invoice.amount, invoice.currency)}
                      </div>
                      <div className="font-mono text-sm text-muted-foreground min-w-[100px]">
                        {formatInvoiceCurrency(
                          invoice.amountOwing ?? Math.max((invoice.amount ?? 0) - (invoice.amountPaid ?? 0), 0),
                          invoice.currency
                        )}
                      </div>
                        <StatusBadge status={invoice.status} context="invoice" />
                        <div className="text-sm text-muted-foreground hidden md:block">
                          {invoice.description}
                        </div>
                      </div>
                      {invoice.hostedUrl && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openExternalUrl(invoice.hostedUrl)}
                        >
                          {invoice.status === 'open' ? 'Pay Now' : 'View'}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No invoices yet.</p>
                  <p className="text-sm">Your first invoice will appear after the billing cycle starts.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin Active Subscriptions */}
        {showAdminAggregates && (
          <Card className="cyber-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Active Subscriptions (All Users)
              </CardTitle>
              <CardDescription>
                Breakdown of active subscriptions by customer and plan.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {adminSubscriptions.length === 0 ? (
                <div className="text-muted-foreground text-sm">No active subscriptions found.</div>
              ) : (
                adminSubscriptions.map((acct) => (
                  <Card key={acct.email} className="border-border/50">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="space-y-1">
                          <p className="text-sm font-mono font-semibold">{acct.email}</p>
                          {acct.fullName && (
                            <p className="text-xs text-muted-foreground">{acct.fullName}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {acct.breakdown.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No active plans</p>
                      ) : (
                        acct.breakdown.map((item) => (
                          <div key={`${acct.email}-${item.label}`} className="flex justify-between text-sm">
                            <span>{item.count}x {item.label}</span>
                            <span className="font-mono">{formatCurrency(item.count * item.price)}</span>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Active Subscriptions (non-admin view) */}
        {!showAdminAggregates && (
          <Card className="cyber-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Active Subscriptions
              </CardTitle>
              <CardDescription>
                All domains with monthly billing. Charged on the 1st of each month.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {domainsLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading...</div>
              ) : totalDomains > 0 ? (
                <div className="space-y-3">
                  {domains?.map((domain) => (
                    <div
                      key={domain.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center flex-wrap gap-2 sm:gap-3">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            domain.status === 'active'
                              ? 'bg-[hsl(var(--ss-success))]'
                              : isPaymentNeeded(domain.status)
                                ? 'bg-[hsl(var(--ss-warning))]'
                                : 'bg-[hsl(var(--ss-neutral))]'
                          }`}
                        />
                        <span className="font-mono break-all">{domain.domain_name}</span>
                        <StatusBadge status={domain.status} />
                      </div>
                      <div className="flex items-center gap-3 w-full sm:w-auto sm:ml-auto justify-between sm:justify-end">
                        <span className="text-sm text-muted-foreground flex-1 sm:flex-none">
                          {formatCurrency(getDomainPrice(domain))}/month
                        </span>
                        <div className="flex items-center gap-2 sm:gap-3">
                          {domain.tier === 'basic' && !domain.security_addon_tier && !isPaymentNeeded(domain.status) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/portal/add-domain?base=hosting_basic&addon=essential&domain_id=${domain.id}`)}
                              >
                                Add Essential
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/portal/add-domain?base=hosting_basic&addon=premium&domain_id=${domain.id}`)}
                              >
                                Add Premium
                              </Button>
                            </>
                          )}
                          {isPaymentNeeded(domain.status) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={resumingDomainId === domain.id}
                                onClick={() => handleResumePayment(domain.id)}
                              >
                                {resumingDomainId === domain.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Opening...
                                  </>
                                ) : (
                                  <>
                                    <span className="hidden sm:inline">{isImpersonationView ? 'Resend Payment Email' : 'Resume Payment'}</span>
                                    <span className="sm:hidden">$</span>
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto sm:ml-0"
                                disabled={deletingDomainId === domain.id}
                              >
                                {deletingDomainId === domain.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove site</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Remove <strong>{domain.domain_name}</strong>? This cancels protection coverage and billing for this site.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <div className="py-2 space-y-3">
                              <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                <div>
                                  Plan:{' '}
                                  <span className="text-foreground">
                                      {getDomainLabel(domain)}
                                  </span>
                                </div>
                                <div>
                                    {isImpersonationView ? 'Removal flow:' : 'Cancellation fee:'}{' '}
                                    {isImpersonationView ? (
                                      <span className="text-foreground">
                                        Customer confirmation email (no cancellation fee) by default
                                      </span>
                                    ) : (
                                      <span className="text-foreground">
                                        {domain.tier === 'dev'
                                          ? 'No cancellation fee under reviewed pricing terms'
                                          : formatCurrency(CANCELLATION_FEES[getTierType(domain)])}
                                      </span>
                                    )}
                                  </div>
                                  {isImpersonationView ? (
                                    <div>Force remove is logged and requires a reason.</div>
                                  ) : domain.tier === 'dev' ? (
                                    <div>No partial-month refund applies. Future billing stops from the next billing cycle after removal.</div>
                                  ) : (
                                    <div>Reason: covers initial setup and configuration. No partial-month refund applies after removal unless required by law.</div>
                                  )}
                                </div>
                                <Label htmlFor={`confirm-${domain.id}`} className="text-sm text-muted-foreground">
                                  Type the domain name to confirm:
                                </Label>
                                <Input
                                  id={`confirm-${domain.id}`}
                                  value={confirmDomainInput}
                                  onChange={(e) => setConfirmDomainInput(e.target.value)}
                                  placeholder={domain.domain_name}
                                  className="mt-2"
                                />
                                {isImpersonationView ? (
                                  <>
                                    <label className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(impersonationForceRemove[domain.id])}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: checked }));
                                          if (!checked) {
                                            setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
                                          }
                                        }}
                                      />
                                      <span>Force remove immediately (skip customer confirmation email).</span>
                                    </label>
                                    {Boolean(impersonationForceRemove[domain.id]) && (
                                      <div>
                                        <Label htmlFor={`force-reason-${domain.id}`} className="text-sm text-muted-foreground">
                                          Force remove reason (required)
                                        </Label>
                                        <Input
                                          id={`force-reason-${domain.id}`}
                                          value={impersonationForceReason[domain.id] || ''}
                                          onChange={(e) => setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: e.target.value }))}
                                          placeholder="Provide the operational reason for immediate removal"
                                          className="mt-2"
                                        />
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <label className="flex items-start gap-2 text-xs text-muted-foreground">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(confirmCancelFees[domain.id])}
                                      onChange={(e) => setConfirmCancelFees((prev) => ({ ...prev, [domain.id]: e.target.checked }))}
                                    />
                                    <span>
                                      {domain.tier === 'dev'
                                        ? 'I understand no cancellation fee applies under the reviewed pricing terms and that no partial-month refund is issued for the current billing period.'
                                        : 'I understand the cancellation fee will be charged to my payment method and that no partial-month refund applies after removal unless required by law.'}
                                    </span>
                                  </label>
                                )}
                              </div>
                              <AlertDialogFooter>
                                <AlertDialogCancel
                                  onClick={() => {
                                    setConfirmDomainInput('');
                                    setConfirmCancelFees((prev) => ({ ...prev, [domain.id]: false }));
                                    setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: false }));
                                    setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
                                  }}
                                >
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteDomain(domain)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  disabled={
                                    isImpersonationView
                                      ? Boolean(impersonationForceRemove[domain.id]) && !(impersonationForceReason[domain.id] || '').trim()
                                      : !confirmCancelFees[domain.id]
                                  }
                                >
                                  {isImpersonationView
                                    ? (impersonationForceRemove[domain.id] ? 'Force remove site' : 'Send confirmation email')
                                    : 'Remove site'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No subscriptions. Add a domain to get started.
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
      <CurrencyDock />
    </div>
  );
}
